import { useState, useEffect, useCallback } from 'react';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import { toast } from 'sonner';
import { Loader2, ArrowUpDown } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const pf = async (endpoint, options = {}) => {
  const res = await fetch(`${API_URL}/api/platform${endpoint}`, {
    ...options, credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...options.headers },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Request failed');
  }
  return res.json();
};

const PlatformSubscriptions = () => {
  const [subs, setSubs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');

  const loadSubs = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const data = await pf(`/subscriptions${params}`);
      setSubs(data.subscriptions);
      setTotal(data.total);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { loadSubs(); }, [loadSubs]);

  const forceAction = async (subId, action) => {
    try {
      await pf(`/subscriptions/${subId}/force-${action}`, { method: 'POST' });
      toast.success(`Subscription ${action}d`);
      loadSubs();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const statusColor = (s) => {
    if (s === 'active') return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
    if (s === 'trial') return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
    if (s === 'cancelled') return 'bg-red-500/10 text-red-400 border-red-500/20';
    return 'bg-slate-700/30 text-slate-400 border-slate-600';
  };

  return (
    <div className="space-y-4" data-testid="platform-subscriptions">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-slate-100">Subscriptions</h1>
          <p className="text-xs text-slate-500">{total} total subscriptions</p>
        </div>
        <select
          className="h-8 px-2 text-xs bg-slate-900 border border-slate-800 rounded text-slate-300"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="platform-sub-status-filter"
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="trial">Trial</option>
          <option value="cancelled">Cancelled</option>
          <option value="expired">Expired</option>
        </select>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-slate-500" /></div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800 hover:bg-transparent">
                <TableHead className="text-[10px] text-slate-500 font-medium">Company</TableHead>
                <TableHead className="text-[10px] text-slate-500 font-medium">Plan</TableHead>
                <TableHead className="text-[10px] text-slate-500 font-medium">Price</TableHead>
                <TableHead className="text-[10px] text-slate-500 font-medium">Status</TableHead>
                <TableHead className="text-[10px] text-slate-500 font-medium">Payment</TableHead>
                <TableHead className="text-[10px] text-slate-500 font-medium">Started</TableHead>
                <TableHead className="text-[10px] text-slate-500 font-medium">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {subs.map(s => (
                <TableRow key={s.subscription_id} className="border-slate-800/50 hover:bg-slate-800/30" data-testid={`platform-sub-row-${s.subscription_id}`}>
                  <TableCell className="text-xs text-slate-300">{s.company_name}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-[10px] border-slate-700 text-slate-400">{s.plan_name}</Badge>
                  </TableCell>
                  <TableCell className="text-xs text-slate-400">Rs.{s.plan_price}/mo</TableCell>
                  <TableCell>
                    <Badge className={`text-[10px] ${statusColor(s.status)}`}>{s.status}</Badge>
                  </TableCell>
                  <TableCell className="text-xs text-slate-500">{s.payment_status || '-'}</TableCell>
                  <TableCell className="text-[10px] text-slate-500">
                    {s.start_date ? new Date(s.start_date).toLocaleDateString() : '-'}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {s.status !== 'active' && (
                        <button
                          className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 transition-colors"
                          onClick={() => forceAction(s.subscription_id, 'activate')}
                          data-testid={`force-activate-${s.subscription_id}`}
                        >
                          Activate
                        </button>
                      )}
                      {s.status === 'active' && (
                        <button
                          className="text-[10px] px-2 py-0.5 rounded bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"
                          onClick={() => forceAction(s.subscription_id, 'deactivate')}
                          data-testid={`force-deactivate-${s.subscription_id}`}
                        >
                          Deactivate
                        </button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {subs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-xs text-slate-500 py-8">No subscriptions found</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
};

export default PlatformSubscriptions;
